
import React, { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Button, Card, Paper, Divider, IconButton,
  Tooltip, CircularProgress, Alert, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, Accordion, AccordionSummary, AccordionDetails, Select,
  MenuItem, FormControl, InputLabel, Switch, FormControlLabel,
  Stack, useMediaQuery, useTheme, LinearProgress, List, ListItem,
  ListItemIcon, ListItemText, alpha,
} from "@mui/material";
import {
  Edit, Delete, AddCircleOutline, Add, ExpandMore,
  Visibility, VisibilityOff, Save, Cancel, MenuBook as MenuBookIcon,
  PictureAsPdf as PdfIcon, UploadFile as UploadFileIcon,
  DeleteOutline as DeleteMaterialIcon,
} from "@mui/icons-material";
import axios from "axios";
import TopBar from "../globalComponents/topbar";
import Footer from "../globalComponents/footer";

const API_BASE = "https://backend-global-1vq9.onrender.com/api/lessons";

// ── Brand palette ────────────────────────────────────────────────────────────
const C = {
  primary: "#0A2E8A",
  primaryDark: "#061E5C",
  primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC",
  primarySofter: "#F2F5FE",
  secondary: "#10682A",
  secondaryDark: "#0A4A1C",
  secondaryLight: "#2E9148",
  secondarySoft: "#E3F2E8",
  secondarySofter: "#F0F8F3",
  bg: "#FAFBFC",
  paper: "#FFFFFF",
  border: "#E5E9F0",
  borderSoft: "#EEF1F6",
  textPrimary: "#0D1117",
  textSecondary: "#5B6B7E",
  textMuted: "#8A9BB0",
  warning: "#D97706",
  warningSoft: "#FEF3C7",
  warningBorder: "#FDE68A",
  error: "#DC2626",
  errorDark: "#B91C1C",
  errorSoft: "#FEE2E2",
  errorBorder: "#FECACA",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5, fontSize: 14,
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.primary },
    "&.Mui-focused fieldset": { borderColor: C.primary, borderWidth: 2 },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: C.primary },
};

const DIFFICULTY_COLORS = {
  beginner:     { bg: C.secondarySoft, color: C.secondary },
  intermediate: { bg: C.warningSoft,   color: C.warning },
  advanced:     { bg: C.errorSoft,     color: C.error },
};

// ── Lesson card ───────────────────────────────────────────────────────────────
function LessonCard({ lesson, onEdit, onDelete, onTogglePublish, truncateDescription }) {
  const diff = DIFFICULTY_COLORS[lesson.difficulty] || DIFFICULTY_COLORS.beginner;

  return (
    <Paper elevation={0} sx={{
      mb: 2, borderRadius: 3, border: `1px solid ${C.border}`, overflow: "hidden",
      bgcolor: C.paper,
      transition: "box-shadow 0.2s, transform 0.2s",
      "&:hover": { boxShadow: "0 6px 20px rgba(13,17,23,0.07)", transform: "translateY(-1px)" },
    }}>
      <Box sx={{
        height: 4,
        background: lesson.isPublished
          ? `linear-gradient(90deg, ${C.secondary}, ${C.secondaryLight})`
          : `linear-gradient(90deg, ${C.border}, ${C.textMuted})`,
      }} />
      <Box sx={{ p: { xs: 2, sm: 2.5 } }}>

        {/* Header */}
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={1.25} gap={1}>
          <Typography fontWeight={700} fontSize={{ xs: 14, sm: 15 }} color={C.textPrimary} lineHeight={1.35} flex={1}>
            {lesson.title}
          </Typography>
          <Chip
            icon={lesson.isPublished
              ? <Visibility sx={{ fontSize: "13px !important" }} />
              : <VisibilityOff sx={{ fontSize: "13px !important" }} />}
            label={lesson.isPublished ? "Published" : "Draft"}
            size="small"
            sx={{
              height: 22, fontSize: 10, fontWeight: 700, flexShrink: 0,
              bgcolor: lesson.isPublished ? C.secondarySoft : C.borderSoft,
              color: lesson.isPublished ? C.secondary : C.textMuted,
              border: `1px solid ${lesson.isPublished ? alpha(C.secondary, 0.25) : C.border}`,
              "& .MuiChip-label": { px: 0.75 },
              "& .MuiChip-icon": { color: lesson.isPublished ? C.secondary : C.textMuted },
            }}
          />
        </Stack>

        {/* Meta chips */}
        <Stack direction="row" flexWrap="wrap" gap={0.75} mb={1.25}>
          {lesson.category && (
            <Chip label={lesson.category} size="small"
              sx={{ height: 20, fontSize: 10, fontWeight: 600,
                bgcolor: C.primarySoft, color: C.primary,
                border: `1px solid ${alpha(C.primary, 0.2)}`,
                "& .MuiChip-label": { px: 0.75 } }} />
          )}
          {lesson.difficulty && (
            <Chip label={lesson.difficulty} size="small"
              sx={{ height: 20, fontSize: 10, fontWeight: 600, textTransform: "capitalize",
                bgcolor: diff.bg, color: diff.color, "& .MuiChip-label": { px: 0.75 } }} />
          )}
          <Chip label={`${lesson.sections?.length || 0} Sections`} size="small"
            sx={{ height: 20, fontSize: 10, bgcolor: C.borderSoft, color: C.textSecondary, "& .MuiChip-label": { px: 0.75 } }} />
          <Chip label={`${lesson.totalDuration || 0} mins`} size="small"
            sx={{ height: 20, fontSize: 10, bgcolor: C.borderSoft, color: C.textSecondary, "& .MuiChip-label": { px: 0.75 } }} />
        </Stack>

        {/* Description */}
        <Typography fontSize={12} color={C.textSecondary} lineHeight={1.65} mb={1.5}>
          {truncateDescription(lesson.description, 30)}
        </Typography>

        {/* Sections accordion */}
        {lesson.sections?.length > 0 && (
          <Accordion elevation={0}
            sx={{ border: `1px solid ${C.border}`, borderRadius: "10px !important", mb: 1.5,
              "&:before": { display: "none" }, "&.Mui-expanded": { m: "0 0 12px 0" } }}>
            <AccordionSummary expandIcon={<ExpandMore sx={{ fontSize: 18, color: C.primary }} />}
              sx={{ minHeight: 40, "&.Mui-expanded": { minHeight: 40 }, "& .MuiAccordionSummary-content": { my: 0.75 } }}>
              <Typography fontSize={12} fontWeight={700} color={C.primary}>
                View Sections ({lesson.sections.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 2 }}>
              <Stack spacing={0.5}>
                {lesson.sections.map((s, i) => (
                  <Stack key={i} direction="row" alignItems="center" spacing={0.75}>
                    <Typography fontSize={12} color={C.textSecondary}>
                      {i + 1}. {s.title} — {s.duration} mins
                    </Typography>
                    {s.materials?.some(m => m.type === "pdf") && (
                      <Chip
                        icon={<PdfIcon sx={{ fontSize: "11px !important" }} />}
                        label={`${s.materials.filter(m => m.type === "pdf").length} PDF`}
                        size="small"
                        sx={{ height: 18, fontSize: 10, bgcolor: C.errorSoft, color: C.error,
                          border: `1px solid ${C.errorBorder}`, "& .MuiChip-label": { px: 0.5 },
                          "& .MuiChip-icon": { color: C.error } }}
                      />
                    )}
                  </Stack>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Actions */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Edit Lesson">
              <IconButton size="small" onClick={() => onEdit(lesson)}
                sx={{ color: C.primary, p: 0.75, borderRadius: 2, "&:hover": { bgcolor: C.primarySoft } }}>
                <Edit sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Lesson">
              <IconButton size="small" onClick={() => onDelete(lesson._id)}
                sx={{ color: C.error, p: 0.75, borderRadius: 2, "&:hover": { bgcolor: C.errorSoft } }}>
                <Delete sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Stack>
          <Button size="small"
            variant={lesson.isPublished ? "outlined" : "contained"}
            onClick={() => onTogglePublish(lesson._id, lesson.isPublished)}
            sx={{
              borderRadius: 2, textTransform: "none", fontWeight: 700, fontSize: 12, px: 1.75,
              ...(lesson.isPublished
                ? { borderColor: C.border, color: C.textSecondary,
                    "&:hover": { borderColor: C.textSecondary, bgcolor: C.borderSoft } }
                : { bgcolor: C.secondary, color: "#fff", boxShadow: "none",
                    "&:hover": { bgcolor: C.secondaryDark, boxShadow: `0 4px 12px ${C.secondary}30` } }),
            }}>
            {lesson.isPublished ? "Unpublish" : "Publish"}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}

// ── Lesson form ───────────────────────────────────────────────────────────────
function LessonForm({
  newLesson, onChange, sections, editMode, loading, error, success,
  onSubmit, onCancel, onAddSection, onEditSection, onDeleteSection,
  truncateDescription,
}) {
  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{
            width: 4, height: 22,
            background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
            borderRadius: 1,
          }} />
          <Typography fontWeight={700} fontSize={15} color={C.textPrimary}>
            {editMode ? "Edit Lesson" : "Create a New Lesson"}
          </Typography>
        </Stack>
        {editMode && onCancel && (
          <Button size="small" onClick={onCancel} startIcon={<Cancel sx={{ fontSize: 15 }} />}
            sx={{ textTransform: "none", fontSize: 12, color: C.error }}>
            Cancel
          </Button>
        )}
      </Stack>
      <Divider sx={{ mb: 2.5, borderColor: C.borderSoft }} />

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5, fontSize: 13 }} onClose={() => {}}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2.5, fontSize: 13 }} onClose={() => {}}>{success}</Alert>}

      <Stack spacing={2}>
        <TextField label="Lesson Title *" name="title" value={newLesson.title} onChange={onChange} fullWidth size="small" sx={fieldSx} />
        <TextField label="Description *" name="description" value={newLesson.description} onChange={onChange}
          multiline rows={3} fullWidth size="small" sx={fieldSx} />
        <TextField label="Thumbnail URL" name="thumbnail" value={newLesson.thumbnail} onChange={onChange} fullWidth size="small" sx={fieldSx} />
        <TextField label="Category" name="category" value={newLesson.category} onChange={onChange}
          placeholder="e.g. Programming, Marketing, Design" fullWidth size="small" sx={fieldSx} />

        <FormControl fullWidth size="small" sx={fieldSx}>
          <InputLabel>Difficulty Level</InputLabel>
          <Select name="difficulty" value={newLesson.difficulty} onChange={onChange} label="Difficulty Level">
            <MenuItem value="beginner">Beginner</MenuItem>
            <MenuItem value="intermediate">Intermediate</MenuItem>
            <MenuItem value="advanced">Advanced</MenuItem>
          </Select>
        </FormControl>

        {/* Sections */}
        <Divider sx={{ borderColor: C.borderSoft }} />
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography fontWeight={700} fontSize={14} color={C.textPrimary}>
            Sections ({sections.length})
          </Typography>
          <Button size="small" variant="outlined" startIcon={<Add sx={{ fontSize: 15 }} />}
            onClick={onAddSection}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: 12,
              borderColor: C.primary, color: C.primary, "&:hover": { bgcolor: C.primarySoft, borderColor: C.primaryDark } }}>
            Add Section
          </Button>
        </Stack>

        {sections.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 2.5, fontSize: 12 }}>
            No sections yet. Click "Add Section" to create course content.
          </Alert>
        ) : (
          <Stack spacing={1}>
            {sections.map((section, index) => (
              <Card key={index} elevation={0}
                sx={{ p: 1.75, borderRadius: 2.5, border: `1px solid ${C.border}`, bgcolor: C.primarySofter }}>
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <Box flex={1} minWidth={0}>
                    <Typography fontWeight={700} fontSize={13} color={C.textPrimary}>
                      {index + 1}. {section.title}
                    </Typography>
                    <Typography fontSize={11} color={C.textMuted} noWrap>
                      {truncateDescription(section.description, 12)}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" mt={0.25}>
                      <Typography fontSize={11} color={C.primary} fontWeight={600}>
                        {section.duration} mins · {section.isRequired ? "Required" : "Optional"}
                      </Typography>
                      {section.materials?.filter(m => m.type === "pdf").length > 0 && (
                        <Chip
                          icon={<PdfIcon sx={{ fontSize: "11px !important" }} />}
                          label={`${section.materials.filter(m => m.type === "pdf").length} PDF`}
                          size="small"
                          sx={{ height: 18, fontSize: 10, bgcolor: C.errorSoft, color: C.error,
                            border: `1px solid ${C.errorBorder}`, "& .MuiChip-label": { px: 0.5 },
                            "& .MuiChip-icon": { color: C.error } }}
                        />
                      )}
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={0.25}>
                    <IconButton size="small" onClick={() => onEditSection(index)}
                      sx={{ p: 0.5, color: C.primary, borderRadius: 1.5, "&:hover": { bgcolor: C.primarySoft } }}>
                      <Edit sx={{ fontSize: 15 }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => onDeleteSection(index)}
                      sx={{ p: 0.5, color: C.error, borderRadius: 1.5, "&:hover": { bgcolor: C.errorSoft } }}>
                      <Delete sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Stack>
                </Stack>
              </Card>
            ))}
          </Stack>
        )}

        {/* Submit */}
        <Button variant="contained" size="large"
          startIcon={editMode ? <Save sx={{ fontSize: 17 }} /> : <AddCircleOutline sx={{ fontSize: 17 }} />}
          onClick={onSubmit} disabled={loading}
          sx={{
            borderRadius: 2.5, textTransform: "none", fontWeight: 700, fontSize: 14,
            background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
            boxShadow: "none",
            "&:hover": { boxShadow: `0 4px 14px ${C.primary}40` },
            "&.Mui-disabled": { opacity: 0.6 },
          }}>
          {loading ? "Processing…" : editMode ? "Update Lesson" : "Create Lesson"}
        </Button>
      </Stack>
    </Box>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const MentorLessons = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const user = JSON.parse(localStorage.getItem("user"));
  const mentorId = user?.id || user?._id;

  const [lessons, setLessons] = useState([]);
  const [newLesson, setNewLesson] = useState({ title: "", description: "", thumbnail: "", category: "", difficulty: "beginner" });
  const [sections, setSections] = useState([]);
  const [currentSection, setCurrentSection] = useState({ title: "", description: "", content: "", videoLink: "", duration: 0, isRequired: true });
  const [editMode, setEditMode] = useState(false);
  const [editLessonId, setEditLessonId] = useState(null);
  const [sectionDialog, setSectionDialog] = useState(false);
  const [editingSectionIndex, setEditingSectionIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingLessons, setLoadingLessons] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showFormMobile, setShowFormMobile] = useState(false);
  const lessonsPerPage = 4;

  const [pdfFile, setPdfFile] = useState(null);
  const [pdfTitle, setPdfTitle] = useState("");
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfUploadProgress, setPdfUploadProgress] = useState(0);

  useEffect(() => { fetchLessons(); }, []);

  const fetchLessons = async () => {
    try {
      setLoadingLessons(true);
      const res = await axios.get(`${API_BASE}/all?mentorId=${mentorId}`);
      setLessons(res.data.data || []);
    } catch { setError("Failed to load lessons."); }
    finally { setLoadingLessons(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewLesson((prev) => ({ ...prev, [name]: value }));
  };

  const handleSectionChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentSection((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const resetForm = () => {
    setEditMode(false); setEditLessonId(null);
    setNewLesson({ title: "", description: "", thumbnail: "", category: "", difficulty: "beginner" });
    setSections([]);
    setPdfFile(null); setPdfTitle(""); setPdfUploadProgress(0);
    if (isMobile) setShowFormMobile(false);
  };

  const uploadPdfToSection = async (lessonId, sectionId) => {
    if (!pdfFile) return;
    setPdfUploading(true);
    setPdfUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append("pdf", pdfFile);
      formData.append("title", pdfTitle.trim() || pdfFile.name);

      await axios.post(
        `${API_BASE}/${lessonId}/sections/${sectionId}/pdf`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (e.total) setPdfUploadProgress(Math.round((e.loaded / e.total) * 100));
          },
        }
      );

      setSuccess("PDF uploaded successfully!");
      setPdfFile(null);
      setPdfTitle("");
      setPdfUploadProgress(0);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to upload PDF.");
      throw err;
    } finally {
      setPdfUploading(false);
    }
  };

  const deletePdfMaterial = async (lessonId, sectionId, materialId) => {
    if (!window.confirm("Remove this PDF from the section?")) return;
    try {
      await axios.delete(`${API_BASE}/${lessonId}/sections/${sectionId}/pdf/${materialId}`);
      setSuccess("PDF removed.");
      await fetchLessons();
      const refreshed = lessons.find(l => l._id === lessonId);
      if (refreshed) {
        const sec = refreshed.sections.find(s => s._id === sectionId);
        if (sec) setCurrentSection(sec);
      }
    } catch {
      setError("Failed to remove PDF.");
    }
  };

  const addOrUpdateSection = async () => {
    if (!currentSection.title || !currentSection.description) {
      setError("Section title and description are required");
      return;
    }
    try {
      if (editingSectionIndex !== null) {
        const updated = [...sections];
        updated[editingSectionIndex] = { ...currentSection };
        setSections(updated);

        if (editMode && editLessonId && currentSection._id) {
          await axios.put(
            `${API_BASE}/${editLessonId}/sections/${currentSection._id}`,
            currentSection,
            { headers: { "Content-Type": "application/json" } }
          );
          if (pdfFile) {
            await uploadPdfToSection(editLessonId, currentSection._id);
          }
          await fetchLessons();
        }
        setSuccess("Section updated!");

      } else {
        setSections([...sections, currentSection]);

        if (editMode && editLessonId) {
          const res = await axios.post(
            `${API_BASE}/${editLessonId}/sections`,
            currentSection,
            { headers: { "Content-Type": "application/json" } }
          );

          const createdSections = res.data?.data?.sections;
          const newSectionId = createdSections?.[createdSections.length - 1]?._id;

          if (pdfFile && newSectionId) {
            await uploadPdfToSection(editLessonId, newSectionId);
          }
          await fetchLessons();
        }
        setSuccess("Section added!");
      }

      setCurrentSection({ title: "", description: "", content: "", videoLink: "", duration: 0, isRequired: true });
      setPdfFile(null);
      setPdfTitle("");
      setPdfUploadProgress(0);
      setSectionDialog(false);
      setEditingSectionIndex(null);
    } catch {
      setError("Failed to save section.");
    }
  };

  const editSection = (index) => {
    setCurrentSection({ ...sections[index] });
    setEditingSectionIndex(index);
    setPdfFile(null);
    setPdfTitle("");
    setPdfUploadProgress(0);
    setSectionDialog(true);
  };

  const deleteSection = async (index) => {
    if (!window.confirm("Delete this section?")) return;
    try {
      const s = sections[index];
      setSections(sections.filter((_, i) => i !== index));
      setSuccess("Section removed!");
      if (editMode && editLessonId && s._id) {
        await axios.delete(`${API_BASE}/${editLessonId}/sections/${s._id}`);
        await fetchLessons();
      }
    } catch { setError("Failed to delete section."); fetchLessons(); }
  };

  const handleAddLesson = async () => {
    if (!newLesson.title || !newLesson.description) { setError("Please fill in all required fields."); return; }
    setLoading(true); setError(""); setSuccess("");
    try {
      const res = await axios.post(`${API_BASE}/create`,
        { mentorId, ...newLesson, sections, certificate: { enabled: true, passingScore: 100 } });
      setLessons((prev) => [res.data.data, ...prev]);
      setSuccess("Lesson created successfully!");
      resetForm();
    } catch (err) { setError(err.response?.data?.message || "Failed to create lesson."); }
    finally { setLoading(false); }
  };

  const handleUpdateLesson = async () => {
    if (!newLesson.title || !newLesson.description) { setError("Please fill in all required fields."); return; }
    setLoading(true); setError(""); setSuccess("");
    try {
      const res = await axios.put(`${API_BASE}/update/${editLessonId}`, newLesson);
      setLessons((prev) => prev.map((l) => (l._id === editLessonId ? res.data.data : l)));
      setSuccess("Lesson updated successfully!"); resetForm();
    } catch { setError("Failed to update lesson."); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this lesson? All sections and progress will be deleted.")) return;
    try {
      await axios.delete(`${API_BASE}/${id}`);
      setLessons((prev) => prev.filter((l) => l._id !== id));
      setSuccess("Lesson deleted successfully!");
    } catch { setError("Failed to delete lesson."); }
  };

  const togglePublish = async (lessonId, currentStatus) => {
    try {
      const res = await axios.patch(`${API_BASE}/${lessonId}/publish`);
      setLessons((prev) => prev.map((l) => (l._id === lessonId ? res.data.data : l)));
      setSuccess(`Lesson ${currentStatus ? "unpublished" : "published"} successfully!`);
    } catch { setError("Failed to update publish status."); }
  };

  const handleEditClick = (lesson) => {
    setEditMode(true); setEditLessonId(lesson._id);
    setNewLesson({ title: lesson.title, description: lesson.description,
      thumbnail: lesson.thumbnail || "", category: lesson.category || "",
      difficulty: lesson.difficulty || "beginner" });
    setSections(lesson.sections || []);
    setPdfFile(null); setPdfTitle(""); setPdfUploadProgress(0);
    if (isMobile) setShowFormMobile(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const truncateDescription = (text, wordLimit = 30) => {
    if (!text) return "";
    const words = text.split(" ");
    return words.length > wordLimit ? words.slice(0, wordLimit).join(" ") + "…" : text;
  };

  const closeSectionDialog = () => {
    setSectionDialog(false);
    setEditingSectionIndex(null);
    setPdfFile(null);
    setPdfTitle("");
    setPdfUploadProgress(0);
  };

  const indexOfLast = currentPage * lessonsPerPage;
  const currentLessons = lessons.slice(indexOfLast - lessonsPerPage, indexOfLast);
  const totalPages = Math.ceil(lessons.length / lessonsPerPage);

  const existingSectionPdfs = editingSectionIndex !== null && editMode && editLessonId
    ? (sections[editingSectionIndex]?.materials?.filter(m => m.type === "pdf") || [])
    : [];

  const formProps = {
    newLesson, onChange: handleChange, sections, editMode, loading,
    error, success,
    onSubmit: editMode ? handleUpdateLesson : handleAddLesson,
    onCancel: resetForm,
    onAddSection: () => {
      setCurrentSection({ title: "", description: "", content: "", videoLink: "", duration: 0, isRequired: true });
      setEditingSectionIndex(null);
      setPdfFile(null); setPdfTitle(""); setPdfUploadProgress(0);
      setSectionDialog(true);
    },
    onEditSection: editSection,
    onDeleteSection: deleteSection,
    truncateDescription,
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: C.bg }}>
      <TopBar />

      <Box sx={{ flex: 1, px: { xs: 2, sm: 3, md: 5, lg: 10 }, py: { xs: 3, md: 4 } }}>

        {/* Page header — hero gradient */}
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            borderRadius: 3, overflow: "hidden",
            background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
            p: { xs: 3, sm: 4 },
            mb: { xs: 2.5, sm: 3 },
            color: "#fff",
            boxShadow: `0 6px 24px ${C.primary}30`,
          }}
        >
          <Box sx={{
            position: "absolute", top: "-40%", right: "-10%",
            width: { xs: 200, sm: 280 }, height: { xs: 200, sm: 280 },
            borderRadius: "50%",
            background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
            pointerEvents: "none",
          }} />
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}
            sx={{ position: "relative", zIndex: 1 }}>
            <Box flex={1} minWidth={0}>
              <Typography sx={{
                fontSize: { xs: 11, sm: 12 }, fontWeight: 700, letterSpacing: 1.5,
                textTransform: "uppercase", color: "rgba(255,255,255,0.7)", mb: 0.75,
              }}>
                Mentor Console
              </Typography>
              <Typography sx={{
                fontWeight: 800, fontSize: { xs: 22, sm: 28, md: 32 },
                color: "#fff", lineHeight: 1.15, mb: 0.75, letterSpacing: -0.5,
              }}>
                Manage Your Lessons
              </Typography>
              <Typography sx={{
                color: "rgba(255,255,255,0.85)",
                fontSize: { xs: 13, sm: 14 }, maxWidth: 520, lineHeight: 1.5,
              }}>
                Create, edit and publish lessons for your mentees
              </Typography>
            </Box>

            {isMobile && !showFormMobile && (
              <Button
                variant="contained" startIcon={<AddCircleOutline sx={{ fontSize: 17 }} />}
                onClick={() => { resetForm(); setShowFormMobile(true); }}
                sx={{
                  bgcolor: "#fff", color: C.primary,
                  borderRadius: 2.5, fontWeight: 700, fontSize: 13,
                  textTransform: "none", boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
                  "&:hover": { bgcolor: "#fff" },
                }}
              >
                New Lesson
              </Button>
            )}
          </Stack>
        </Paper>

        {/* Mobile form */}
        {isMobile && showFormMobile && (
          <Paper elevation={0} sx={{
            borderRadius: 3, border: `1px solid ${C.border}`,
            bgcolor: C.paper, p: 2.5, mb: 3,
            boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
          }}>
            <LessonForm {...formProps} />
            {!editMode && (
              <Button fullWidth size="small" onClick={() => setShowFormMobile(false)}
                sx={{ mt: 1.5, textTransform: "none", fontSize: 12, color: C.textMuted }}>
                Cancel
              </Button>
            )}
          </Paper>
        )}

        <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">

          {/* Lessons list */}
          <Box flex={1} minWidth={0} order={{ xs: 1, md: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Typography fontWeight={700} fontSize={15} color={C.textPrimary}>Your Lessons</Typography>
                {!loadingLessons && lessons.length > 0 && (
                  <Chip
                    label={lessons.length} size="small"
                    sx={{
                      bgcolor: C.primarySoft, color: C.primary,
                      fontWeight: 700, height: 22,
                      "& .MuiChip-label": { px: 1 },
                    }}
                  />
                )}
              </Stack>
            </Stack>
            <Divider sx={{ mb: 2, borderColor: C.borderSoft }} />

            {loadingLessons ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <CircularProgress size={40} sx={{ color: C.primary }} />
              </Box>
            ) : lessons.length === 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center"
                minHeight={200} bgcolor={C.paper} borderRadius={3} border={`1px solid ${C.border}`}>
                <MenuBookIcon sx={{ fontSize: 48, color: C.textMuted, opacity: 0.5, mb: 1 }} />
                <Typography fontWeight={700} color={C.textPrimary} fontSize={14}>No lessons yet</Typography>
                <Typography variant="caption" color={C.textSecondary}>Create your first lesson to get started</Typography>
              </Box>
            ) : (
              <>
                {currentLessons.map((lesson) => (
                  <LessonCard key={lesson._id} lesson={lesson}
                    onEdit={handleEditClick} onDelete={handleDelete}
                    onTogglePublish={togglePublish} truncateDescription={truncateDescription} />
                ))}

                {totalPages > 1 && (
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mt={2} flexWrap="wrap" gap={2}>
                    <Button variant="outlined" disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => p - 1)}
                      sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 700, fontSize: 13,
                        borderColor: C.primary, color: C.primary,
                        "&:hover": { borderColor: C.primaryDark, bgcolor: C.primarySoft },
                        "&.Mui-disabled": { borderColor: C.borderSoft, color: C.textMuted } }}>
                      ← Previous
                    </Button>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      {Array.from({ length: totalPages }, (_, i) => (
                        <Box key={i} onClick={() => setCurrentPage(i + 1)}
                          sx={{ width: i + 1 === currentPage ? 24 : 8, height: 8, borderRadius: 1,
                            bgcolor: i + 1 === currentPage ? C.primary : alpha(C.primary, 0.2),
                            cursor: "pointer", transition: "all 0.22s" }} />
                      ))}
                    </Stack>
                    <Button variant="outlined" disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage((p) => p + 1)}
                      sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 700, fontSize: 13,
                        borderColor: C.primary, color: C.primary,
                        "&:hover": { borderColor: C.primaryDark, bgcolor: C.primarySoft },
                        "&.Mui-disabled": { borderColor: C.borderSoft, color: C.textMuted } }}>
                      Next →
                    </Button>
                  </Stack>
                )}
              </>
            )}
          </Box>

          {/* Desktop form sidebar */}
          {!isMobile && (
            <Paper elevation={0} sx={{
              position: "relative",
              width: 360, flexShrink: 0, order: 2,
              borderRadius: 3, border: `1px solid ${C.border}`,
              bgcolor: C.paper,
              p: 3, position: "sticky", top: 24,
              maxHeight: "85vh", overflowY: "auto",
              boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
              overflowX: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0, left: 0, right: 0,
                height: "3px",
                background: `linear-gradient(90deg, ${C.secondary}, ${C.secondary}88)`,
              },
            }}>
              <LessonForm {...formProps} />
            </Paper>
          )}
        </Stack>
      </Box>

      {/* ── Section Dialog ── */}
      <Dialog
        open={sectionDialog}
        onClose={closeSectionDialog}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16, color: C.textPrimary }}>
          {editingSectionIndex !== null ? "Edit Section" : "Add New Section"}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2} mt={0.5}>
            <TextField label="Section Title *" name="title" value={currentSection.title}
              onChange={handleSectionChange} fullWidth size="small" sx={fieldSx} />
            <TextField label="Description *" name="description" value={currentSection.description}
              onChange={handleSectionChange} multiline rows={2} fullWidth size="small" sx={fieldSx} />
            <TextField label="Content (Detailed Text)" name="content" value={currentSection.content}
              onChange={handleSectionChange} multiline rows={4} fullWidth size="small" sx={fieldSx} />
            <TextField label="Video Link" name="videoLink" value={currentSection.videoLink}
              onChange={handleSectionChange} fullWidth size="small" placeholder="YouTube, Vimeo, etc." sx={fieldSx} />
            <TextField label="Duration (minutes)" name="duration" type="number" value={currentSection.duration}
              onChange={handleSectionChange} fullWidth size="small" sx={fieldSx} />
            <FormControlLabel
              control={
                <Switch
                  checked={currentSection.isRequired} onChange={handleSectionChange}
                  name="isRequired" size="small"
                  sx={{
                    "& .Mui-checked": { color: `${C.primary} !important` },
                    "& .Mui-checked + .MuiSwitch-track": { bgcolor: `${C.primary} !important` },
                  }}
                />
              }
              label={<Typography fontSize={13} fontWeight={600} color={C.textSecondary}>Required Section</Typography>}
              sx={{ m: 0, pl: 0.5 }}
            />

            {/* ── PDF Upload Section ── */}
            <Divider sx={{ borderColor: C.borderSoft }} />
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <PdfIcon sx={{ fontSize: 18, color: C.error }} />
                <Typography fontWeight={700} fontSize={13} color={C.textPrimary}>
                  PDF Materials
                </Typography>
                {!editMode && (
                  <Chip label="Available after lesson is saved" size="small"
                    sx={{ height: 18, fontSize: 10, bgcolor: C.warningSoft, color: C.warning,
                      border: `1px solid ${C.warningBorder}`, "& .MuiChip-label": { px: 0.75 } }} />
                )}
              </Stack>

              {editMode && existingSectionPdfs.length > 0 && (
                <Box mb={1.5}>
                  <Typography fontSize={11} color={C.textSecondary} mb={0.75} fontWeight={600}>
                    Attached PDFs:
                  </Typography>
                  <List dense disablePadding>
                    {existingSectionPdfs.map((m) => (
                      <ListItem
                        key={m._id}
                        disableGutters
                        sx={{ px: 1.25, py: 0.5, borderRadius: 2,
                          border: `1px solid ${C.errorBorder}`,
                          bgcolor: alpha(C.errorSoft, 0.5), mb: 0.5 }}
                        secondaryAction={
                          <Tooltip title="Remove PDF">
                            <IconButton
                              edge="end" size="small"
                              onClick={() => deletePdfMaterial(editLessonId, currentSection._id, m._id)}
                              sx={{ p: 0.4, color: C.error, "&:hover": { bgcolor: C.errorSoft } }}
                            >
                              <DeleteMaterialIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <PdfIcon sx={{ fontSize: 16, color: C.error }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={m.title}
                          primaryTypographyProps={{ fontSize: 12, fontWeight: 600, color: C.textPrimary, noWrap: true }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {editMode ? (
                <Box>
                  <TextField
                    label="PDF Title (optional)"
                    value={pdfTitle}
                    onChange={(e) => setPdfTitle(e.target.value)}
                    fullWidth size="small" sx={{ ...fieldSx, mb: 1.25 }}
                    placeholder="Defaults to filename if left blank"
                  />

                  <Box
                    component="label"
                    htmlFor="pdf-upload-input"
                    sx={{
                      display: "flex", alignItems: "center", gap: 1.25,
                      p: 1.5, borderRadius: 2.5, cursor: "pointer",
                      border: `2px dashed ${pdfFile ? C.primary : C.border}`,
                      bgcolor: pdfFile ? C.primarySoft : C.primarySofter,
                      transition: "border-color 0.2s, background 0.2s",
                      "&:hover": { borderColor: C.primary, bgcolor: C.primarySoft },
                    }}
                  >
                    <UploadFileIcon sx={{ fontSize: 22, color: pdfFile ? C.primary : C.textMuted, flexShrink: 0 }} />
                    <Box flex={1} minWidth={0}>
                      {pdfFile ? (
                        <>
                          <Typography fontSize={12} fontWeight={700} color={C.primary} noWrap>
                            {pdfFile.name}
                          </Typography>
                          <Typography fontSize={11} color={C.textSecondary}>
                            {(pdfFile.size / 1024 / 1024).toFixed(2)} MB
                          </Typography>
                        </>
                      ) : (
                        <Typography fontSize={12} color={C.textMuted} fontWeight={600}>
                          Click to select a PDF file (max 20 MB)
                        </Typography>
                      )}
                    </Box>
                    {pdfFile && (
                      <IconButton
                        size="small"
                        onClick={(e) => { e.preventDefault(); setPdfFile(null); setPdfTitle(""); }}
                        sx={{ p: 0.4, color: C.error, flexShrink: 0 }}
                      >
                        <Cancel sx={{ fontSize: 15 }} />
                      </IconButton>
                    )}
                    <input
                      id="pdf-upload-input"
                      type="file"
                      accept="application/pdf"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          if (file.size > 20 * 1024 * 1024) {
                            setError("PDF must be under 20 MB.");
                            return;
                          }
                          setPdfFile(file);
                          if (!pdfTitle) setPdfTitle(file.name.replace(/\.pdf$/i, ""));
                        }
                        e.target.value = "";
                      }}
                    />
                  </Box>

                  {pdfUploading && (
                    <Box mt={1}>
                      <Stack direction="row" justifyContent="space-between" mb={0.5}>
                        <Typography fontSize={11} color={C.textSecondary} fontWeight={600}>Uploading…</Typography>
                        <Typography fontSize={11} fontWeight={700} color={C.primary}>{pdfUploadProgress}%</Typography>
                      </Stack>
                      <LinearProgress variant="determinate" value={pdfUploadProgress}
                        sx={{
                          height: 6, borderRadius: 3, bgcolor: C.borderSoft,
                          "& .MuiLinearProgress-bar": {
                            background: `linear-gradient(90deg, ${C.primary}, ${C.primaryLight})`,
                            borderRadius: 3,
                          },
                        }} />
                    </Box>
                  )}
                </Box>
              ) : (
                <Alert severity="info" sx={{ borderRadius: 2, fontSize: 11, py: 0.5 }}>
                  Save the lesson first, then edit the section to attach PDF files.
                </Alert>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={closeSectionDialog}
            sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 700, fontSize: 13,
              color: C.textSecondary, border: `1px solid ${C.border}`, px: 2,
              "&:hover": { borderColor: C.textSecondary, bgcolor: C.borderSoft } }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={addOrUpdateSection}
            disabled={pdfUploading}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontWeight: 700, fontSize: 13,
              background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
              boxShadow: "none",
              "&:hover": { boxShadow: `0 4px 14px ${C.primary}40` },
              "&.Mui-disabled": { opacity: 0.6 },
            }}
          >
            {pdfUploading ? "Uploading PDF…" : editingSectionIndex !== null ? "Update Section" : "Add Section"}
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </Box>
  );
};

export default MentorLessons;
